import { loadScriptFromCDN } from '../helpers/utils.js';
import { BlueprintShape } from './blueprint-graph/vertex/shape.js';
import { BlueprintText } from './blueprint-graph/vertex/text.js';
import { BlueprintImage } from './blueprint-graph/vertex/image.js';
import { BlueprintLine } from './blueprint-graph/edge/line.js';
import { BlueprintGrid } from './blueprint-graph/grid.js';

import * as constants from './blueprint-graph/constants.js';

export class BlueprintGraph {
  /** @property {BPDefaultOptions} */
  static defaultOptions = {
    aspectRatio: 21 / 9,
    fallbacks: {
      shapeColor: 'rgb(100,100,100)',
      controlsColor: 'rgb(100,100,100)',
    },
    grid: {
      spacing: 20,
      width: 8000,
      height: 4000,
    },
  };

  /**
   * @property {boolean} initializing - A flag to determine whether changes should be ignored
   */
  #initializing = true;

  /**
   * @property {{ states: Object[], currentIndex: number }} history - An object containing all states since initialization, as well as the current active state
   */
  #history = {
    states: [],
    currentIndex: 0,
  };

  /**
   * This must be kept in-sync with any modifications to the current objects in fabric, whether adding or removing.
   *
   * @property {Array<Object>} cells - An array that mirrors the fabric internal array so that we can store BlueprintShape instances to provide extensions and helpful utils to the BlueprintGraph consumer, while still allowing for easy ineteroperability with fabric.
   */
  #cells = [];

  /**
   * @property {HTMLElement|null} #parentEl - The parent element of the `<canvas>` used to build the graph. Event handlers are assigned to this for keyboard and context events, because of this it will be given a tabIndex value of `1` if tabIndex isn't set.. If you need to allow events to bubble due to custom UI elements that may float above the canvas (and need to position them relative), wrap the canvas itself in an additional element.
   * @example
     ```html
       <div class="graph-wrapper">
         <div class="canvas-wrapper">
           <canvas></canvas>
         </div>
         <omni-tile></omni-tile>
       </div>
     ```
   */
  #parentEl;

  /**
   * @property {HTMLElement|null} canvasEl - The html <canvas> element that will be replaced with a fabric canvas
   */
  #canvasEl;

  /**
   * @property {Object.<string, Function>} listeners - A hashmap of event names and external handlers passed into the constructor as part of the options object
   * @see {BPGraphOptions}
   */
  #listeners = {};

  /**
   * @property {Object.<string, any>} config - Storage for configuration options, for the graph and grid, passed into the constructor
   * @see {BPGraphOptions}
   */
  #config = {};

  /**
   * @param {HTMLCanvasElement} container - The target canvas to build the graph on
   * @param {BPGraphOptions} config
   */
  constructor(container, config, readonly) {
    this.#config = config;

    /** @todo this should probably be called something different, because it's technically a canvas */
    this.#canvasEl = container;

    // for responsively setting width/height based on aspectRatio
    this.#parentEl = container.parentElement;

    if (this.#parentEl.tabIndex < 0) this.#parentEl.tabIndex = 1;

    /** @type {BlueprintCanvas} */
    // @ts-ignore This can only be null on initialization/import failure
    this.canvas = null;

    /**
     * @todo make all this event handling a service of it's own?
     * @todo maybe add a wrapper webcomponent that just implements this and
     * forwards all of the event calls as regular JS events.
     * @todo Need other events? maybe zoomin/zoomout/
     * @type {BPListeners}
     */
    this.#initListeners();

    this.#loadFabricAndInit();

    this.readonly = readonly;
  }

  get containerWidth() {
    return this.#parentEl?.getBoundingClientRect().width || 0;
  }

  get cells() {
    return this.#cells;
  }

  /** @returns {BlueprintCell[]} */
  get selection() {
    const fabricSelections = this.canvas.getActiveObjects();

    return fabricSelections.map(obj => this.#cells.find(c => c.ref === obj));
  }

  get currentZoom() {
    /** @todo not sure this is the correct method */
    return this.canvas.getZoom();
  }

  /**
   * this just returns whether you can undo/redo and keeps the history encapsulated
   * @todo It's a bit confusing to have a private #history, and a public history, that are different things, change the name?
   */
  get history() {
    return {
      canUndo: this.#history.currentIndex > 0,
      canRedo: this.#history.states.length - 1 !== this.#history.currentIndex,
    };
  }

  /**
   * NOTE: Grid is automatically excluded using
   * excludeFromExport in BlueprintGrid class
   */
  get state() {
    return {
      ...this.canvas.toJSON([
        /** Keep these Fabric internal props so they're restored on load */
        'selectable',
        'hasControls',
        'hasBorders',
        'evented',
        'snapAngle',

        /** Blueprint custom internal properties */
        /** Shape text */
        'bpShapeText',
        'bpShapeTextStyles',
        'bpShapeTextFill',
        'bpShapeTextAlign',
        /** Lines */
        'bpLineArrow',
      ]),
    };
  }

  /**
   * @TODO: If this stays public, it should accept BlueprintVertices
   * @param {fabric.Object} shape - The shape whose index to grab
   */
  getObjectIndex(shape) {
    return this.canvas.getObjects().indexOf(shape);
  }

  /**
   * @param {Object} graphState - The previous graph state to load
   * @todo document any weirdness
   * @todo Implement guids
   */
  loadJSON = graphState =>
    new Promise(res => {
      this.#initializing = true;

      this.canvas.loadFromJSON(graphState, () => {
        this.grid = new BlueprintGrid(
          this,
          this.#config.grid || BlueprintGraph.defaultOptions.grid,
        );

        this.canvas
          .getObjects()
          // ignore grid related objects
          .filter(obj => !this.grid.is(obj))
          // setup blueprint objects
          .forEach(obj => {
            // disable selectable if user is readonly
            if (this.readonly) obj.set('selectable', false);

            const fabricShapeTypes = Object.values(constants.SHAPE_TYPES);
            const isShape = fabricShapeTypes.some(
              type => type.toLowerCase() === obj.type,
            );
            const additionalProps = {};
            additionalProps.readonly = this.readonly;
            let CellClass;

            /** @todo Maybe we just detect whether it's a vertex or an edge and the parent class can determine the type and return it? */
            if (isShape) {
              CellClass = BlueprintShape;
            } else if (obj.type === 'image') {
              CellClass = BlueprintImage;
            } else if (obj.type === 'i-text') {
              CellClass = BlueprintText;
            } else if (obj.type === 'line') {
              CellClass = BlueprintLine;
              additionalProps.controls = {
                fill: 'white',
                stroke: this.#getDefault('controlsColor'),
              };
            }

            if (CellClass) {
              /** @todo figure out ts lint issue */
              const newCell = new CellClass(this.canvas, {
                // @ts-ignore
                fabricObject: obj,
                ...additionalProps,
              });
              this.#upsertInternalCell(newCell);
            }
          });

        this.#initializing = false;
        this.render();

        // if initial load, add to history states, otherwise we're in an undo/redo cycle
        if (!this.#history.states.includes(graphState)) {
          this.#history.states.push(graphState);
        }

        res();
      });
    });

  /** @todo document */
  undo = () => {
    const { currentIndex, states } = this.#history;

    if (currentIndex === 0) return;
    this.#history.currentIndex -= 1;
    this.loadJSON(states[this.#history.currentIndex]);
  };

  /** @todo document */
  redo = () => {
    const { currentIndex, states } = this.#history;

    if (currentIndex === states.length - 1) return;
    this.#history.currentIndex += 1;
    this.loadJSON(states[this.#history.currentIndex]);
  };

  /**
   * Center the viewport on 0, 0 based on the canvas dimensions and set zoom to 1
   */
  recenter = () => {
    const { canvas } = this;
    this.canvas.setViewportTransform([
      1,
      0,
      0,
      1,
      canvas.width / 2,
      canvas.height / 2,
    ]);
  };

  /**
   * @param {string} name - The event to listen to
   * @param {Function} callback - The event handler
   * @todo add explanation to doc comments
   */
  on(name, callback) {
    if (
      this.#listeners[name].find(
        /** @param {Function} listener */
        listener => listener === callback,
      )
    )
      return this;

    if (Array.isArray(callback)) {
      this.#listeners[name] = this.#listeners[name].concat(callback);
    } else {
      this.#listeners[name].push(callback);
    }

    return this;
  }

  destroy() {
    /** @todo Does "destroy" make sense here without tearing down fabric and the canvas? */
    this.#removeInternalListeners();
  }

  /**
   * @param {string} src - The image URL
   * @param {Object=} fabricOptions - Objects to pass to fabric
   */
  async addImage(src, fabricOptions = {}) {
    const { canvas } = this;
    const img = new BlueprintImage(canvas, { src, fabricOptions });

    await img.loaded;

    img.centerInViewport();

    return this.add(img);
  }

  /**
   * @param {string} content - The content to add to the text vector
   * @param {Object=} fabricOptions - Objects to pass to fabric
   */
  addText(content, fabricOptions = {}) {
    const { canvas } = this;
    const text = new BlueprintText(canvas, { content, fabricOptions });

    // Dont override passed in positioning like drag and drop
    if (!fabricOptions.top || !fabricOptions.left) text.centerInViewport();

    return this.add(text);
  }

  /**
   * @param {BPShapeKey} shapeType - One of the keys from {@link constants~shapeType}
   * @param {Object=} fabricOptions - Options for the shape that fabric expects
   * @param {Object=} textOptions - Options for the text added to the shape
   */
  addShape(shapeType, fabricOptions = {}, textOptions = {}) {
    const { canvas } = this;
    const width = Math.round(canvas.getHeight() / 4);
    const height = width;

    const shape = new BlueprintShape(canvas, {
      shapeType,
      fabricOptions: {
        width,
        height,
        fill:
          this.#config.defaults.shapeColor ||
          BlueprintGraph.defaultOptions.fallbacks.shapeColor,
        ...fabricOptions,
      },
      textOptions,
    });

    // Dont override passed in positioning like drag and drop
    if (!fabricOptions.top || !fabricOptions.left) shape.centerInViewport();

    return this.add(shape);
  }

  /**
   * @param {BlueprintShape} oldShape
   * @param {BPShapeKey} newShapeType
   */
  replaceShape(oldShape, newShapeType) {
    if (!oldShape || !newShapeType?.trim()) return;

    /** @todo: Maybe we should have a "replace cell" as an additional helper?  We only need this here for the ability to swap between the shapes specifically */
    const { canvas } = this;

    const activeIndex = this.getObjectIndex(oldShape.ref);
    const nextShape = oldShape.toShape(newShapeType);

    canvas.insertAt(nextShape.ref, activeIndex);
    canvas.discardActiveObject();
    canvas.setActiveObject(nextShape.ref);

    this.#upsertInternalCell(nextShape, oldShape);

    canvas.renderAll();
  }

  /**
   * @param {Object} lineConfig -
   * @param {Object.<string, { start: {x: number, y:number}, end: {x: number, y:number}}>} lineConfig.coords - The start and end coordinates
   * @param {Object=} fabricOptions - Objects to pass to fabric
   */
  addLine(lineConfig, fabricOptions = {}) {
    const { canvas } = this;
    const line = new BlueprintLine(canvas, {
      controls: {
        fill: 'white',
        stroke: this.#getDefault('controlsColor'),
      },
      ...lineConfig,
      fabricOptions,
    });

    /** @todo Fix the line placement */
    // Dont override passed in positioning like drag and drop
    if (!fabricOptions.top || !fabricOptions.left || !lineConfig.coords)
      line.centerInViewport();

    return this.add(line);
  }

  /**
   * @param {BlueprintCell} cell - The fabric object behind the vertex/edge
   * @todo Implement guids
   */
  add(cell) {
    const { canvas } = this;

    this.#upsertInternalCell(cell);
    canvas.add(cell.ref).renderAll();
    this.selectCells(cell);

    return cell;
  }

  /**
   * Zooms the canvas in, by a default amount unless specified.
   * @param {number=} interval=0.2 - Positive float to increment by
   */
  zoomIn = (interval = 0.2) => {
    this.#handleZoom(interval);
  };

  /**
   * Zooms the canvas out, by a default amount unless specified.
   * @param {number=} interval=-0.2 - Negative float to decrement by
   */
  zoomOut = (interval = -0.2) => {
    this.#handleZoom(interval);
  };

  /**
   * Sets the zoom level of the canvas to 100% unless specified.
   * @param {number=} level=1 - The zoom level to set
   */
  setZoom(level = 1) {
    this.canvas.setZoom(level);
  }

  /**
   * @todo Popupwidth is the outlier here, we need to determine a value to check or a mechanism to use to do a proximity check with the container.
   */
  // Window width used to match edit popup's 25vw width
  getPopupCoords = ({
    popupWidth = window.innerWidth / 4,
    popupHeight = window.innerHeight / 2,
    cell = this.selection[0],
  } = {}) => {
    if (!cell) return;

    const { canvas } = this;
    const xMargin = 14;
    const { ref: c } = Array.isArray(cell) ? cell.at(-1) : cell;
    // Get the relative x position of the bounding rectangle relative to the canvas frame
    const { top: cellTop, left: cellLeft, width } = c.getBoundingRect(true);
    let left = cellLeft + width + xMargin;
    let top = cellTop;

    // Temporary transform the point to do proper calculations
    let point = fabric.util.transformPoint(
      // Add some padding distance to the width for calculations & default to positioning to the right of the cell
      new fabric.Point(left, top),
      canvas.viewportTransform || [],
    );

    /**
     * If the distance between the right side of the cell and the canvas
     * edge is greater than the width of the popup, position the pop-up to the left
     */
    if (point.x + popupWidth > canvas.width)
      // The double margin on opposite side accounts for it being present in the original left definition
      left -= width + popupWidth + xMargin * 2;

    // If the popup extends lower than the viewport, subtract the difference to place on bottom
    if (
      // Account for distance between window top and canvas top
      point.y +
        popupHeight +
        (this.#parentEl?.getBoundingClientRect()?.top || 0) >
      window.innerHeight - xMargin // reusing margin for edge distance
    )
      top = -(popupHeight / 2);

    if (left !== cellLeft || top !== cellTop)
      point = fabric.util.transformPoint(
        new fabric.Point(left, top),
        canvas.viewportTransform || [],
      );

    // eslint-disable-next-line consistent-return
    return point;
  };

  /**
   * @param {BlueprintCell} cell
   * @param {boolean} updateSelection - whether select the cell immediately after cloning
   */
  async clone(cell, updateSelection = true) {
    const { canvas } = this;
    const clonedCell = cell.clone();

    // Conditionally await load for async cells like Image
    // eslint-disable-next-line no-unused-expressions
    (await clonedCell.loaded) ?? Promise.resolve();

    canvas.add(clonedCell.ref);

    if (updateSelection) {
      this.selectCells(clonedCell);
    }

    this.#upsertInternalCell(clonedCell);

    return clonedCell;
  }

  async cloneSelected() {
    const { canvas, selection } = this;
    const active = canvas.getActiveObject();

    if (active.type === 'activeSelection') {
      const { x: aX, y: aY } = active.getCenterPoint();

      const clonedCells = await Promise.all(
        selection.map(async cell => {
          const { x: cX, y: cY } = cell.getCenterPoint();
          const clone = cell.clone();

          // Conditionally await load for async cells like Image
          // eslint-disable-next-line no-unused-expressions
          (await clone.loaded) ?? Promise.resolve();

          // re-align since it's group-relative
          clone.setCenterPoint({ x: aX + cX, y: aY + cY });
          this.add(clone);

          return clone;
        }),
      );

      const newSelection = this.selectCells(clonedCells);

      /** @todo: When we have groups of text/shapes, we may need to calculate transformation matrics */
      // manually set relevant properties instead of playing with matrices
      newSelection.set('angle', active.angle);
      newSelection.set('scaleX', active.scaleX);
      newSelection.set('scaleY', active.scaleY);

      newSelection.setPositionByOrigin(
        { x: aX + 20, y: aY + 20 },
        'center',
        'center',
      );
    } else {
      const [selectedCell] = selection;
      this.clone(selectedCell);
    }
  }

  /**
   * @param {BlueprintCell} cell - The object to delete
   * @param {boolean} [animate=true] - Whether to animate deletion
   * @param {boolean} [rerender=true] - Whether to trigger a rerender after deletion
   */
  async delete(cell, animate = true, rerender = true) {
    /** @todo: Should we have logging, if not error handling? */
    if (!cell) return;

    const { canvas } = this;

    // promisify this callback-based method
    await new Promise(onComplete => {
      if (animate) {
        // @ts-ignore: callback not typed properly
        canvas.fxRemove(cell.ref, onComplete);
      } else {
        canvas.remove(cell.ref);
        onComplete();
      }
    });

    if (rerender) {
      canvas.discardActiveObject().requestRenderAll();
    }

    this.#deleteInternalCell(cell);
  }

  async deleteSelected() {
    const { canvas } = this;
    const selectedObjects = this.selection;

    canvas.discardActiveObject();

    /**
     * fxRemove is async and takes a callback, we need to await the
     * removals before discarding the current selection
     */
    await Promise.all(selectedObjects?.map(v => this.delete(v)) || []);
  }

  /**
   * Make an active selection of a cell, or group of cells.
   * @param {BlueprintCell|BlueprintCell[]} target - The target cell, or array of cells, to select
   * @todo Maybe this will be a type of {BlueprintShape} when structure is more solid?
fabric.Object|fabric.Object[]
   */
  selectCells(target) {
    const { canvas } = this;
    const selection = !Array.isArray(target)
      ? target.ref
      : new fabric.ActiveSelection(
          target.map(c => c.ref),
          { canvas },
        );

    canvas.discardActiveObject().setActiveObject(selection).requestRenderAll();

    return selection;
  }

  /**
   * @param {boolean} [request=true] - Whether to request a re-render or force it
   * @todo Not sure request is a necessary param, could possibly just use one or the other.
   */
  render(request = false) {
    this.canvas[request ? 'requestRenderAll' : 'renderAll']();
  }

  /**
   * Return the value of either a default that was passed in, or the
   * static fallback defaults
   * @param {string} k - the key name to use to retrieve the value
   */
  #getDefault = k =>
    this.#config?.defaults?.[k] ||
    BlueprintGraph.defaultOptions.fallbacks.controlsColor[k];

  /** @param {BlueprintCell} cell - A cell to remove from the #cells array */
  #deleteInternalCell = cell => {
    this.#cells = this.#cells.filter(c => c !== cell);
  };

  /**
   * @param {BlueprintCell} cell - The cell to add to the internal #cells array
   * @param {BlueprintCell=} replacement - The optional cell to be replaced
   */
  #upsertInternalCell = (cell, replacement) => {
    const index =
      this.#cells.length && replacement
        ? this.#cells.findIndex(c => c.ref === replacement.ref)
        : this.#cells.length;

    this.#cells.splice(index, replacement ? 1 : 0, cell);
  };

  #initListeners = () => {
    /** @const {Object.<string, Function[]|Function>} configListeners - A hashmap of listener arrays */
    const configListeners = this.#config.listeners;

    /**
     * @param {Function[]|Function} listener
     * @return {Function[]}
     */
    const ensureArray = listener => {
      if (!listener) return [];

      return Array.isArray(listener) ? listener : [listener];
    };

    /** @todo We're missing some representation of object related events (object:x) among other things */
    this.#listeners = {
      interaction: ensureArray(configListeners.interaction),
      selectioncleared: ensureArray(configListeners.selectioncleared),
      selectionupdated: ensureArray(configListeners.selectionupdated),
      selectioncreated: ensureArray(configListeners.selectioncreated),
      mouseup: ensureArray(configListeners.mouseup),
      mousedown: ensureArray(configListeners.mousedown),
      mouseover: ensureArray(configListeners.mouseover),
      mousemove: ensureArray(configListeners.mousemove),
      mousewheel: ensureArray(configListeners.mousewheel),
      dragstart: ensureArray(configListeners.dragstart),
      dragend: ensureArray(configListeners.dragend),
      dragover: ensureArray(configListeners.dragover),
      click: ensureArray(configListeners.click),
      dblclick: ensureArray(configListeners.dblclick),
      keydown: ensureArray(configListeners.keydown),
      contextmenu: ensureArray(configListeners.contextmenu),
      change: ensureArray(configListeners.change),
      dropbefore: ensureArray(configListeners.dropbefore),
    };
  };

  #init = async () => {
    const {
      grid = BlueprintGraph.defaultOptions.grid,
      initialState,
      listeners: { init = undefined } = {},
    } = this.#config;

    /** @type BlueprintCanvas */
    const canvas = new fabric.Canvas(this.#canvasEl, {
      fireMiddleClick: !this.readonly,
      fireRightClick: !this.readonly,
      /** @todo: Should this be configurable? */
      preserveObjectStacking: true,
      // imageSmoothingEnabled: false,
    });

    this.canvas = canvas;

    /** Setup initial width/height */
    this.#handleResponsiveCanvas();

    await (initialState ? this.loadJSON(initialState) : Promise.resolve());

    // don't add listeners until JSON is loaded
    this.#addInternalListeners();

    /** @todo Fix positioning */
    this.#handleResponsiveCanvas();

    // set custom control settings
    Object.entries({
      transparentCorners: false,
      cornerColor: 'white',
      /** @todo should we set more of these values this way, like cornerColor? */
      cornerStrokeColor: this.#getDefault('controlsColor'),
      borderColor: this.#getDefault('controlsColor'),
      cornerStrokeWidth: 2,
      cornerSize: 10,
      padding: 2,
      cornerStyle: 'circle',
      borderDashArray: [2, 2],
    }).forEach(([k, v]) => {
      fabric.Object.prototype[k] = v;
    });

    // conditionally add it here unless it was already created during load
    this.grid ||= new BlueprintGrid(this, grid);

    init?.(this);

    // loadJSON sets initializing to false, this sets it on first load
    this.#initializing = false;
  };

  /**
   * @param {string} eventName - The name the handlers are assigned to on the this.#listeners object
   * @param {any[]} args - Arguments to be passed through to the handler
   */
  #dispatchToListeners(eventName, ...args) {
    // if there are no listeners or graph is still being initialized, don't trigger events
    if (!this.#listeners[eventName]?.length || this.#initializing) return;

    this.#listeners[eventName].forEach(
      /** @param {Function} handler */
      handler => handler(...args),
    );
  }

  /**
   * @param {any[]} opt
   */
  #handleDblClick = opt => {
    const { canvas } = this;

    if (this.grid.is(opt.target)) return;

    this.#dispatchToListeners('dblclick', opt, canvas);
  };

  /**
   * @param {any[]} args - fabric arguments for a given event, passed through to the handlers
   */
  #handleHistoryChange = (...args) => {
    const changeHandler = this.#getGenericHandler('change');

    // don't start absorbing states until initialized, or being loaded from JSON
    if (!this.#initializing) {
      this.#history.currentIndex =
        this.#history.currentIndex === 0 && this.#history.states.length === 0
          ? // if on a new graph, with no state yet, set to 0
            0
          : // otherwise increase the current index
            this.#history.currentIndex + 1;

      this.#history.states.push(this.state);

      changeHandler(...args);
    }
  };

  #getSelectionHandler = eventName => () => {
    const { canvas } = this;

    const selection = canvas.getActiveObject();

    if (
      selection.type === 'activeSelection' &&
      selection.getObjects().filter(object => object.type === 'line').length > 0
    ) {
      selection.lockScalingY = true;
      selection.lockScalingX = true;
    }

    this.#dispatchToListeners(eventName, selection, this);
  };

  /**
   * Assign internal listeners to the canvas and parent element.
   * Each individual handler with trigger it's related listener calls.
   * @todo maybe a handler class can exist to hold these methods?
   * @todo There is a different experience for events that have custom code in BlueprintGraph and those that are just forwarded along, in that the ones forwarded just receive fabric arguments instead of BlueprintVertex or BlueprintEdge objects
   */
  #addInternalListeners(canvas = this.canvas) {
    window.addEventListener('resize', this.#handleResponsiveCanvas);

    /**
     * Call `interaction` event before relevant events are handled
     * @param {string} eventName - A key for a listener {@see BPListeners}
     * @param {Function} handler
     * @todo It might be better to determine what sorts of things to pass to the handler based on the type of event... things like `objectSelect` or `rightClicked`, etc.
     */
    const getWrappedHandler =
      (eventName, handler) =>
      /** @param {any[]} args */
      (...args) => {
        this.#dispatchToListeners('interaction', eventName, this, ...args);
        handler(...args);
      };

    canvas.on('mouse:move', this.#handleMouseMove);
    canvas.on('mouse:up', this.#handleMouseUp);

    /** @todo document that dblclick only fires on cells */
    canvas.on('mouse:dblclick', this.#handleDblClick);

    Object.entries({
      'mouse:wheel': this.#handleMouseWheel,
      'mouse:down': this.#handleMouseDown,

      // unified change event
      'object:modified': this.#handleHistoryChange,
      'object:added': this.#handleHistoryChange,
      'object:removed': this.#handleHistoryChange,
      'path:created': this.#handleHistoryChange,

      // generic handlers
      'selection:cleared': this.#getGenericHandler('selectioncleared'),
      'selection:created': this.#getSelectionHandler('selectioncreated'),
      'selection:updated': this.#getSelectionHandler('selectionupdated'),
      'drop:before': this.#getGenericHandler('dropbefore'),
    }).forEach(([eventName, handler]) => {
      // @ts-ignore more trying to work around fabric's type expections
      canvas.on(
        eventName,
        getWrappedHandler(eventName.replace(/:/g, ''), handler),
      );
    });

    Object.entries({
      keydown: this.#handleKeyPress,
      contextmenu: this.#handleContextEvent,
    }).forEach(([eventName, handler]) => {
      // @ts-ignore more trying to work around fabric's type expections
      this.#parentEl?.addEventListener(
        eventName,
        getWrappedHandler(eventName, handler),
      );
    });
  }

  #removeInternalListeners = () => {
    window.removeEventListener('resize', this.#handleResponsiveCanvas);
    this.#parentEl?.removeEventListener('keydown', this.#handleKeyPress);
    this.#parentEl?.removeEventListener(
      'contextmenu',
      this.#handleContextEvent,
    );
  };

  #loadFabricAndInit = async () => {
    // don't create a new script tag if it's loaded
    await (window.fabric
      ? Promise.resolve()
      : loadScriptFromCDN('fabric@5.2.4/dist/fabric.min.js', 'fabric'));

    this.#config.listeners?.load?.(this);

    this.#init();
  };

  /**
   * An arrow function to handle the contextmenu DOM even on the parent element of the canvas
   * This is a DOM event, the callback will NOT receive a fabric options argument
   * @param {MouseEvent} e - context event from right-click
   */
  #handleContextEvent = e => {
    if (!this.#listeners.contextmenu.length) return;

    e.preventDefault();

    const relativePosition = {
      y: e.clientY - this.#parentEl.getClientRects()[0].top,
      x: e.clientX - this.#parentEl.getClientRects()[0].left,
    };

    this.#dispatchToListeners('contextmenu', relativePosition);
  };

  #handleResponsiveCanvas = () => {
    this.canvas.setDimensions({
      width: this.containerWidth,
      height:
        this.containerWidth /
        (this.#config.aspectRatio ?? BlueprintGraph.defaultOptions.aspectRatio),
    });
  };

  /**
   * @todo Do we need default behavior? should some of these just live in child classes?
   * @param {string} eventName
   */
  #getGenericHandler =
    eventName =>
    /** @param {any[]} args - passed through to handlers via listener dispatch */
    (...args) => {
      this.#dispatchToListeners(eventName, ...args);
    };

  /**
   * Set operation based on interaction (right/left-click, drag, etc) & dispatch to listeners
   * @param {Object} opt
   */
  #handleMouseDown = opt => {
    const { e: evt, target } = opt;
    const { canvas } = this;
    const gridTargeted = target && this.grid.is(target);
    const targetIsActiveSelection = target?.type === 'activeSelection';
    // if there's a selection being targeted, use that
    const cell = targetIsActiveSelection
      ? this.selection
      : this.#cells.find(c => c.ref === target);

    // right-click when object is clicked
    if (!targetIsActiveSelection && cell && evt.button === 2 && !gridTargeted) {
      this.selectCells(cell);
    }

    // initializing dragging when using alt key or middle-click
    if (evt.altKey === true || evt.button === 1) {
      canvas.isDragging = true;
      canvas.selection = false;
      canvas.lastPosX = evt.clientX;
      canvas.lastPosY = evt.clientY;
    }

    this.#dispatchToListeners(
      'mousedown',
      opt.e,
      !gridTargeted ? cell : undefined,
      this,
    );
  };

  /**
   * Handle panning & dispatch to listeners
   * @param {Object} opt
   */
  #handleMouseMove = opt => {
    const { e } = opt;
    const { canvas } = this;
    if (canvas.isDragging) {
      const vpt = canvas.viewportTransform || [];
      vpt[4] += e.clientX - (canvas.lastPosX || 0);
      vpt[5] += e.clientY - (canvas.lastPosY || 0);
      canvas.requestRenderAll();
      canvas.lastPosX = e.clientX;
      canvas.lastPosY = e.clientY;
    }

    this.#dispatchToListeners('mousemove', opt, canvas);
  };

  /**
   * Cleanup after mouse up & dispatch to listeners
   * @param {Object} opt
   */
  #handleMouseUp = opt => {
    const { canvas } = this;
    // on mouse up we want to recalculate new interaction
    // for all objects, so we call setViewportTransform
    canvas.setViewportTransform(canvas.viewportTransform || []);
    canvas.isDragging = false;
    canvas.selection = true;

    this.#dispatchToListeners('mouseup', opt, canvas);
  };

  /**
   * Keep zoom level within acceptable range
   * @param {number} level
   * @returns {number}
   */
  #getZoomConstraint = level => {
    if (level > 10) return 10;
    if (level < 0.4) return 0.4;
    return level;
  };

  /**
   * @param {number} val - The floating-point amount to increase or decrease the zoom level.
   */
  #handleZoom = val => {
    const { canvas } = this;
    const [width, height] = [canvas.getWidth(), canvas.getHeight()];

    canvas.zoomToPoint(
      { x: Math.round(width / 2), y: Math.round(height / 2) },
      this.#getZoomConstraint(canvas.getZoom() + val),
    );

    /** @todo dispatch the listener here? */
  };

  /**
   * zoom with respect to mouse position
   * @param {Object} opt
   */
  #handleMouseWheel = opt => {
    const { canvas } = this;
    const delta = opt.e.deltaY;
    let zoom = canvas.getZoom();
    zoom *= 0.999 ** delta;

    zoom = this.#getZoomConstraint(Math.ceil(zoom * 100) / 100);

    canvas.zoomToPoint({ x: opt.e.offsetX, y: opt.e.offsetY }, zoom);

    opt.e.preventDefault();
    opt.e.stopPropagation();

    this.#dispatchToListeners('mousewheel', opt, canvas);
  };

  /**
   * Capture key presses and call action
   * @param {Object} e - The DOM event
   */
  #handleKeyPress = async e => {
    const { key } = e;
    const { canvas } = this;
    const selectedObjects = canvas.getActiveObjects();

    if (key.toLowerCase() === 'delete' && selectedObjects?.length) {
      this.deleteSelected();
    }
  };
}

/** @todo Do these have to live here? */

/**
 * @typedef BlueprintCell
 * @type {import("./blueprint-graph/vertex.js").BlueprintVertex|import("./blueprint-graph/edge.js").BlueprintEdge}
 */

/**
 * @typedef BPShapeKey
 * @type {'square'|'circle'|'triangle'|'diamond'}
 */

/**
 * @typedef BPGraphOptions
 * @property {number} aspectRatio=DefaultOptions.aspectRatio
 * @property {BPListeners} listeners - Keyed event handlers to assign to various events generated by BlueprintGraph
 * @property {BPDefaultFallbacks} defaults - Default values for various blueprint settings like default shape color
 * @property {string} initialState - Previously saved canvas state in JSON format
 * @property {{ spacing: number, width: number, height: number }} grid - options for layout and grid generation
 */

/**
 * @typedef BPDefaultOptions
 * @property {number} aspectRatio=21/9 - The aspect ratio of the element with a width of `100%` of the canvas
 * @property {{ spacing: number, width: number, height: number }} grid - Settings for the background grid
 * @property {BPDefaultFallbacks} fallbacks - Fallback values for optional graph settings container. Expected to be a _float_, the decimal form of the ratio passed in. Defaulted to `21/9`.
 */

/**
 * @typedef BPDefaultFallbacks
 * @property {string} shapeColor="#777" - Fallback color for newly added shapes
 * @property {string} controlsColor="#777" - Fallback color for vertex controls
 */

/**
 * @typedef BPListeners
 * @property {Function|Function[]=} interaction - Any interaction event will dispatch this, passing the callback the event name, a reference to the instance of BlueprintGraph, and all arguments passed into the handler. Interactions include all events that involve a click, context menu, mouse wheel, or keypress.
 * @property {Function} load - Only fired when fabric has surely loaded
 * @property {Function} init - Only fired when the canvas is ready
 * @property {Function|Function[]=} selectioncleared - Vertices have been deselected
 * @property {Function|Function[]=} selectionupdated - The selection has changed
 * @property {Function|Function[]=} selectioncreated - Vertices have been selected
 * @property {Function|Function[]=} mouseup - Canvas event
 * @property {Function|Function[]=} mousedown - Canvas event
 * @property {Function|Function[]=} mouseover - Canvas event
 * @property {Function|Function[]=} mousemove - Canvas event
 * @property {Function|Function[]=} mousewheel - Canvas event
 * @property {Function|Function[]=} dragstart - Canvas event
 * @property {Function|Function[]=} dragend - Canvas event
 * @property {Function|Function[]=} dragover - Canvas event
 * @property {Function|Function[]=} click - Canvas event
 * @property {Function|Function[]=} dblclick - Canvas double click event
 * @property {Function|Function[]=} keydown - event handler, DOM event, listener is assigned to parent container
 * @property {Function|Function[]=} contextmenu - event handler, DOM event, listener is assigned to parent container
 */
